/**
 * @fileoverview Activation Queue - Post-Settle特殊ピース発動キュー
 *
 * 責務：
 * - 変換後の特殊ピース（Bomb2等）の発動タイミング管理
 * - 盤面settled（落下・補充完了）後の発動を保証
 * - 盤面に制御フラグを持たせない設計
 */

import { MinimalErrorTracker } from '../shared/log.js';

const LOG_CONTEXT = 'PSAQ';

/**
 * Post-Settle Activation Queue (PSAQ)
 * settled後に発動すべき特殊ピースのIDリスト
 * @type {Array<number>}
 */
const activationQueue = [];

/**
 * 追加メタデータを保持（待機時間計測やデバッグ用）
 * @type {Map<number, { pieceId: number, specialType?: string|null, chain?: number|null, waveIndex?: number|null, transformIndex?: number|null, enqueuedAt: number }>}
 */
const activationMetadata = new Map();

/**
 * 特殊ピースをpost-settle発動キューに追加
 * @param {number} pieceId - ピースID
 */
export function enqueuePostSettle(pieceId, context = {}) {
  if (pieceId == null) {
    MinimalErrorTracker.warn(LOG_CONTEXT, `Invalid pieceId enqueued: ${pieceId}`);
    return;
  }

  const previousMeta = activationMetadata.get(pieceId) || {};
  const enqueuedAt = previousMeta.enqueuedAt ?? Date.now();
  const mergedMeta = {
    ...previousMeta,
    ...context,
    pieceId,
    enqueuedAt,
  };

  if (activationQueue.includes(pieceId)) {
    activationMetadata.set(pieceId, mergedMeta);
    return;
  }

  activationQueue.push(pieceId);
  activationMetadata.set(pieceId, mergedMeta);
}

/**
 * キューから全ての発動待ちピースIDを取得して消費
 * @returns {Array<number>} ピースIDの配列
 */
export function drainPostSettle() {
  if (activationQueue.length === 0) {
    return [];
  }

  const queue = activationQueue.slice();
  activationQueue.length = 0;

  queue.forEach((pieceId) => {
    activationMetadata.delete(pieceId);
  });

  return queue;
}

/**
 * キューをクリア（ゲームリセット時等）
 */
export function clearActivationQueue() {
  activationQueue.length = 0;
  activationMetadata.clear();
}

/**
 * デバッグ用：現在のキュー状態を取得
 * @returns {Array<number>} 現在のキュー（コピー）
 */
export function getQueueSnapshot() {
  return activationQueue.slice();
}
